// lib/koi-pond-v2/palette.ts
// The locked Phase 1 palette per the 2026-05-25 brief. Each entry has a
// hex string + a role describing what the color is used for. Shaders
// import from this single source of truth; do not hard-code hex values
// inside shader strings.
//
// 13 entries cover all distinct shader inputs. The Phase 1 brief lists
// 12 swatches because mid-water dark/light are displayed in a single
// combined card; the two values remain as separate exports here because
// individual shaders sample them independently (caustic uses
// midWaterDark as fog base; pond floor uses midWaterLight as base
// tint). Do not collapse these into a tuple — the developer-facing API
// surface and the visual-swatch UI are intentionally allowed to differ.

export type PaletteEntry = {
  hex: string;
  role: string;
};

export const palette = {
  deepestWater:     { hex: '#1A1410', role: 'Deepest water — fog endpoint, scene.background at depth' },
  midWaterDark:     { hex: '#2B1F18', role: 'Mid water (dark end of range) — fog mid-distance' },
  midWaterLight:    { hex: '#3D2A1E', role: 'Mid water (light end of range) — pond-floor base tint' },
  surfaceHighlight: { hex: '#8B6B47', role: 'Surface highlight — eye-level water specular' },
  lightShaftCore:   { hex: '#F4D89A', role: 'Godray core — brightest center of light shaft' },
  lightShaftGlow:   { hex: '#E8A661', role: 'Godray edge glow — falloff color' },
  causticAccent:    { hex: '#FBE5BA', role: 'Caustic peak — brightest cells in caustic field' },
  kohakuWhite:      { hex: '#F5EDDC', role: 'Kohaku/sanke/tancho body base — cream-white' },
  kohakuRed:        { hex: '#C8472E', role: 'Kohaku amber patches + sanke amber patches' },
  sankeBlack:       { hex: '#1F1A14', role: 'Sanke sumi accents (warmer-black than karasu)' },
  ogonGold:         { hex: '#D4A258', role: 'Ogon body base (metallic) + karasu dorsal stripe' },
  karasuBlack:      { hex: '#15110D', role: 'Karasu body base (deepest black, no warmth)' },
  tanchoRed:        { hex: '#D63D24', role: 'Tancho head disc — pure single-marking red' },
} as const;

export type PaletteKey = keyof typeof palette;

// GLSL helper — converts the hex string to a vec3 literal at build time
// so shader template strings can interpolate.
export function paletteVec3(key: PaletteKey): string {
  const hex = palette[key].hex.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `vec3(${(r / 255).toFixed(4)}, ${(g / 255).toFixed(4)}, ${(b / 255).toFixed(4)})`;
}
